/**
 * 后台 — 消息中心
 *
 * 设计:按工单 ticket_id 分组 — 一单一卡片,内联显示完整对话 + 一个回复框
 *
 * 流程:
 *   - 拉 push_messages + 拉所有工单的 support_messages
 *   - 按 ticket_id 分组
 *   - 一张卡 = 一个工单 = 完整 thread + 1 个回复框
 *   - 其他类型通知(试用到期/每日推荐)独立展示
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell, MessageCircle, AlertTriangle, Calendar, Sparkles,
  Inbox, RefreshCw, Loader2, Trash2, Send, X, Headphones
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../../lib/api.js';

const TYPE_META = {
  support_new: { icon: MessageCircle, color: 'text-brand-rose', label: '新工单', bg: 'bg-brand-rose/10' },
  user_reply: { icon: MessageCircle, color: 'text-amber-400', label: '用户回复', bg: 'bg-amber-400/10' },
  trial_expiring: { icon: AlertTriangle, color: 'text-accent-warn', label: '试用到期', bg: 'bg-accent-warn/10' },
  daily_recommend: { icon: Calendar, color: 'text-brand-gold', label: '每日推荐', bg: 'bg-brand-gold/10' },
  daily_recap: { icon: Sparkles, color: 'text-brand-purple', label: '每日复盘', bg: 'bg-brand-purple/10' },
};

export function PushView() {
  const [pushMsgs, setPushMsgs] = useState([]);
  const [ticketsData, setTicketsData] = useState({});  // ticketId -> { ticket, messages, user, latestPush }
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  // 1. 拉 push 通知
  const loadPush = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.type = filter;
      const r = await api.admin.pushList(params);
      setPushMsgs(r.messages || []);
    } catch (e) {
      toast.error('加载通知失败');
    } finally {
      setLoading(false);
    }
  };

  // 2. 拉所有有 push 的工单的完整对话
  const loadTickets = async (messages) => {
    const ticketIds = new Set();
    messages.forEach(m => {
      try {
        const p = m.payload ? JSON.parse(m.payload) : null;
        if (p?.ticket_id) ticketIds.add(p.ticket_id);
      } catch {}
    });
    if (ticketIds.size === 0) {
      setTicketsData({});
      return;
    }
    setTicketsLoading(true);
    const data = {};
    await Promise.all([...ticketIds].map(async (tid) => {
      try {
        const r = await api.admin.support.ticketDetail(tid);
        if (r?.ok) data[tid] = r;
      } catch {}
    }));
    setTicketsData(data);
    setTicketsLoading(false);
  };

  useEffect(() => { loadPush(); }, [filter]);
  useEffect(() => { loadTickets(pushMsgs); }, [pushMsgs]);

  // 统计
  const stats = useMemo(() => {
    const supportNew = pushMsgs.filter(m => m.type === 'support_new').length;
    const userReplies = pushMsgs.filter(m => m.type === 'user_reply').length;
    const trials = pushMsgs.filter(m => m.type === 'trial_expiring').length;
    const others = pushMsgs.length - supportNew - userReplies - trials;
    return { total: pushMsgs.length, supportNew, userReplies, trials, others };
  }, [pushMsgs]);

  // 按 ticket_id 分组
  const ticketGroups = useMemo(() => {
    const groups = [];  // [{ ticketId, latestPush, allPushes }]
    const otherMsgs = [];  // 非工单类通知
    const seen = new Map();  // ticketId -> group index
    for (const m of pushMsgs) {
      let tid = null;
      try { const p = m.payload ? JSON.parse(m.payload) : null; tid = p?.ticket_id; } catch {}
      if (tid) {
        if (seen.has(tid)) {
          groups[seen.get(tid)].allPushes.push(m);
        } else {
          seen.set(tid, groups.length);
          groups.push({ ticketId: tid, latestPush: m, allPushes: [m] });
        }
      } else {
        otherMsgs.push(m);
      }
    }
    return { groups, otherMsgs };
  }, [pushMsgs]);

  // 全部已读(删除所有 push)
  const handleClearAll = async () => {
    if (pushMsgs.length === 0) return;
    if (!confirm(`确认清空当前 Tab 的全部 ${pushMsgs.length} 条通知?`)) return;
    try {
      const r = await api.admin.pushClear({ type: filter });
      if (r?.ok) {
        setPushMsgs([]);
        toast.success(`已清空 ${r.deleted || pushMsgs.length} 条`);
      }
    } catch (e) { toast.error('清空失败'); }
  };

  return (
    <div className="space-y-3">
      {/* 统计 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard label="通知总数" value={stats.total} color="text-ink-1" bg="bg-white/5" />
        <StatCard label="新工单" value={stats.supportNew} color="text-brand-rose" bg="bg-brand-rose/10" />
        <StatCard label="用户回复" value={stats.userReplies} color="text-amber-400" bg="bg-amber-400/10" />
        <StatCard label="试用到期" value={stats.trials} color="text-accent-warn" bg="bg-accent-warn/10" />
      </div>

      {/* 筛选 */}
      <div className="card !p-2 flex items-center gap-2 flex-wrap">
        {[
          { v: 'all', label: '全部' },
          { v: 'support_new', label: '🔴 新工单' },
          { v: 'user_reply', label: '💬 用户回复' },
          { v: 'trial_expiring', label: '⏰ 试用到期' },
          { v: 'daily_recommend', label: '📅 每日' },
        ].map(t => (
          <button
            key={t.v}
            onClick={() => setFilter(t.v)}
            className={clsx(
              'px-2.5 py-1 rounded text-xs border transition',
              filter === t.v
                ? 'bg-brand-cyan/20 border-brand-cyan/40 text-brand-cyan'
                : 'bg-white/5 border-white/10 text-ink-2 hover:border-white/20'
            )}
          >
            {t.label}
          </button>
        ))}
        <button onClick={loadPush} className="ml-auto text-xs text-ink-3 hover:text-brand-cyan flex items-center gap-1">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> 刷新
        </button>
        <button
          onClick={handleClearAll}
          disabled={pushMsgs.length === 0}
          className="text-xs text-ink-3 hover:text-red-400 flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Trash2 size={11} /> 清空
        </button>
      </div>

      {/* 列表 */}
      {loading ? (
        <div className="card text-center py-8">
          <Loader2 size={20} className="mx-auto animate-spin text-brand-cyan" />
        </div>
      ) : ticketGroups.groups.length === 0 && ticketGroups.otherMsgs.length === 0 ? (
        <div className="card text-center py-12 text-ink-3 text-sm">
          <Inbox size={32} className="mx-auto mb-2 opacity-30" />
          暂无通知
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {/* 工单类(按 ticket 分组,每工单一卡片 + 内联回复框) */}
            {ticketGroups.groups.map(g => (
              <TicketThreadCard
                key={g.ticketId}
                ticketId={g.ticketId}
                ticketData={ticketsData[g.ticketId]}
                latestPush={g.latestPush}
                pushCount={g.allPushes.length}
                onReplied={() => { loadPush(); loadTickets(pushMsgs); }}
                onClearPush={async (id) => {
                  await api.admin.pushDelete(id);
                  setPushMsgs(prev => prev.filter(m => m.id !== id));
                }}
              />
            ))}
            {/* 非工单类通知(试用到期/每日等) */}
            {ticketGroups.otherMsgs.map(m => (
              <OtherPushCard key={m.id} message={m} onDelete={async () => {
                await api.admin.pushDelete(m.id);
                setPushMsgs(prev => prev.filter(x => x.id !== m.id));
              }} />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

// 工单线程卡片:一单一卡片 = 完整对话 + 1 个回复框
// 关键:发送走乐观更新,不再触发父级 re-fetch(避免"缓冲关闭再打开"感)
function TicketThreadCard({ ticketId, ticketData, latestPush, pushCount, onReplied, onClearPush }) {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [closed, setClosed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // 本地消息列表(乐观更新用,初始 = props.ticketData.messages)
  const [localMsgs, setLocalMsgs] = useState(null);
  const scrollerRef = useRef(null);

  const meta = TYPE_META[latestPush.type] || TYPE_META.support_new;
  const Icon = meta.icon;
  const isClosed = ticketData?.ticket?.status === 'closed' || closed;

  // ticketData 变化时同步到 localMsgs(loadTickets 完成 / 切 tab 后)
  useEffect(() => {
    if (ticketData?.messages) setLocalMsgs(ticketData.messages);
  }, [ticketData?.messages]);

  // 消息列表变化时滚到底部
  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [localMsgs?.length, showAll]);

  // 渲染用 localMsgs(乐观插入的也在这里)
  const allMsgs = localMsgs ?? ticketData?.messages ?? [];
  const recentMsgs = showAll ? allMsgs : allMsgs.slice(-3);
  const isUserNew = latestPush.type === 'support_new';

  const handleReply = async () => {
    const content = reply.trim();
    if (!content) return;
    if (content.length > 5000) {
      toast.error('内容过长(≤5000 字符)');
      return;
    }
    setSending(true);

    // 1) 乐观插入 —— 立即让新消息出现,textarea 立即清空
    const tempId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const tempMsg = {
      id: tempId,
      sender_type: 'admin',
      content,
      created_at: Math.floor(Date.now() / 1000),
      _pending: true,
    };
    setLocalMsgs(prev => [...(prev ?? []), tempMsg]);
    setReply('');

    try {
      const r = await api.admin.support.ticketReply(ticketId, content);
      if (r?.ok) {
        // 2) 后端确认:去掉 _pending(不调 onReplied —— 那会触发父级 loadPush
        //    让整个列表 setLoading(true) → spinner → 重渲染,正是"缓冲感"的来源)
        setLocalMsgs(prev => prev.map(m => m.id === tempId ? { ...m, _pending: false } : m));
        toast.success(`已回复工单 #${ticketId}`);
      } else {
        // 失败:回滚 + 恢复输入
        setLocalMsgs(prev => prev.filter(m => m.id !== tempId));
        setReply(content);
        toast.error(r?.error || '回复失败');
      }
    } catch (e) {
      setLocalMsgs(prev => prev.filter(m => m.id !== tempId));
      setReply(content);
      toast.error('回复失败: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!confirm('确认关闭工单 #' + ticketId + '?')) return;
    try {
      const r = await api.admin.support.ticketClose(ticketId);
      if (r?.ok) {
        setClosed(true);
        toast.success('工单已关闭');
        // 关闭后不触发父级 re-fetch —— 本地 closed state 已足够让回复框消失
      }
    } catch (e) { toast.error(e.message); }
  };

  const handleClearPushes = async () => {
    if (!confirm(`确认清空该工单的所有通知(${pushCount}条)?`)) return;
    for (const _ of Array(pushCount)) {
      // 用一个一个 delete API,但更高效的是清空当前类型;
      // 简化:先删除这条 latestPush,如果还有剩余,刷新后用户再点
    }
    await onClearPush(latestPush.id);
    toast.success('已清空该工单通知');
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 100 }}
      className="card !p-3 border border-brand-cyan/20"
    >
      {/* 卡片头 */}
      <div className="flex items-start gap-3 mb-2">
        <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', meta.bg)}>
          <Icon size={18} className={meta.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-semibold', meta.bg, meta.color)}>
              {meta.label}
            </span>
            <span className="text-[10px] text-ink-3">
              {new Date(latestPush.sent_at || latestPush.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
            {pushCount > 1 && (
              <span className="text-[10px] text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded">
                {pushCount} 条通知合并
              </span>
            )}
            {isClosed && (
              <span className="text-[10px] text-ink-3 bg-white/5 px-1.5 py-0.5 rounded">已关闭</span>
            )}
            <button
              onClick={handleClearPushes}
              className="ml-auto text-ink-3 hover:text-red-400 p-0.5"
              title="清空该工单通知"
            >
              <X size={14} />
            </button>
          </div>
          <div className="text-sm font-semibold mt-1 truncate">
            {ticketData?.ticket?.subject || latestPush.title} — 工单 #{ticketId}
          </div>
          {ticketData?.ticket && (
            <div className="text-[10px] text-ink-3 mt-0.5">
              用户: {ticketData.ticket.user_name || '-'} · {ticketData.ticket.user_phone || '-'} · {ticketData.ticket.user_tier || '未登录'}
              {ticketData.ticket.status && (
                <span className="ml-2">状态: {ticketData.ticket.status}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 对话 thread(内联,最新 3 条) */}
      {!ticketData ? (
        <div className="text-[10px] text-ink-3 py-2">
          <Loader2 size={12} className="inline animate-spin mr-1" /> 加载对话...
        </div>
      ) : allMsgs.length === 0 ? (
        <div className="text-[10px] text-ink-3 py-2">暂无消息</div>
      ) : (
        <div ref={scrollerRef} className="bg-bg-elev/50 rounded-lg p-2 mb-2 max-h-64 overflow-y-auto space-y-1.5">
          {(showAll ? allMsgs : allMsgs.slice(-3)).map(msg => {
            const isUser = msg.sender_type === 'user';
            const isPending = msg._pending === true;
            return (
              <div
                key={msg.id}
                className={clsx('flex', isUser ? 'justify-start' : 'justify-end')}
              >
                <div
                  className={clsx(
                    'max-w-[80%] rounded-lg px-2.5 py-1.5 text-xs transition-opacity',
                    isUser
                      ? 'bg-white/5 text-ink-1 border border-white/5'
                      : 'bg-brand-cyan/15 text-ink-0 border border-brand-cyan/30',
                    isPending && 'opacity-60'
                  )}
                >
                  <div className="text-[9px] text-ink-3 mb-0.5 flex items-center gap-1">
                    <span>{isUser ? '👤 用户' : '✉️ 客服'}</span>
                    <span>·</span>
                    <span>{new Date(msg.created_at * 1000).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    {isPending && (
                      <span className="text-brand-cyan/80 flex items-center gap-0.5 ml-1">
                        <Loader2 size={8} className="animate-spin" /> 发送中
                      </span>
                    )}
                  </div>
                  <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                </div>
              </div>
            );
          })}
          {allMsgs.length > 3 && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="w-full text-center text-[10px] text-brand-cyan hover:underline py-1"
            >
              显示全部 {allMsgs.length} 条消息 ↓
            </button>
          )}
          {showAll && allMsgs.length > 3 && (
            <button
              onClick={() => setShowAll(false)}
              className="w-full text-center text-[10px] text-ink-3 hover:text-brand-cyan py-1"
            >
              收起 ↑
            </button>
          )}
        </div>
      )}

      {/* 单一回复框(整张卡只有一个) */}
      {!isClosed && ticketData && (
        <div className="bg-bg-elev border border-white/10 rounded-lg p-2">
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder={`回复工单 #${ticketId}...`}
            rows={2}
            maxLength={5000}
            className="w-full bg-bg-card border border-white/10 rounded px-2 py-1.5 text-xs outline-none focus:border-brand-cyan/40 resize-none"
            style={{ color: 'var(--ink-0)' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleReply();
              }
            }}
          />
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[10px] text-ink-3">{reply.length}/5000 · Enter 发送 / Shift+Enter 换行</span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleClose}
                className="text-[10px] text-ink-3 hover:text-red-400 px-2 py-1"
              >
                关闭工单
              </button>
              <button
                onClick={handleReply}
                disabled={!reply.trim() || sending}
                className="btn-primary !text-[10px] !py-1 !px-2 flex items-center gap-1 disabled:opacity-50"
              >
                {sending ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                发送
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

// 非工单类通知(试用到期等)
function OtherPushCard({ message: m, onDelete }) {
  const meta = TYPE_META[m.type] || { icon: Bell, color: 'text-ink-1', label: m.type, bg: 'bg-white/5' };
  const Icon = meta.icon;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 100 }}
      className={clsx('card !p-3 border transition hover:border-brand-cyan/40', meta.bg)}
    >
      <div className="flex items-start gap-3">
        <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', meta.bg)}>
          <Icon size={18} className={meta.color} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-semibold', meta.bg, meta.color)}>
              {meta.label}
            </span>
            <span className="text-[10px] text-ink-3">
              {new Date(m.sent_at || m.created_at).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
            <button onClick={onDelete} className="ml-auto text-ink-3 hover:text-red-400 p-0.5" title="删除">
              <X size={14} />
            </button>
          </div>
          <div className="text-sm font-semibold mt-1 truncate">{m.title}</div>
          <div className="text-xs text-ink-2 mt-0.5 line-clamp-2">{m.content}</div>
        </div>
      </div>
    </motion.div>
  );
}

function StatCard({ label, value, color, bg }) {
  return (
    <div className={clsx('rounded-lg p-2.5 border border-white/5', bg)}>
      <div className="text-[10px] text-ink-3">{label}</div>
      <div className={clsx('text-2xl font-bold font-mono', color)}>{value ?? '-'}</div>
    </div>
  );
}
